/**
 * Staff feedback (Krysi, 1 October, #4): jumping person → organisation →
 * partner lost the way back, and a list came back unfiltered. Records and
 * dashboards note where staff have been in this tab, so a record can offer
 * "Back to <previous place>", returning to that list with its filters (they
 * live in the address) or to that record. Tabs within a record are one
 * place, so they never count as somewhere to go back to.
 *
 * @remarks
 * The trail belongs to the signed-in staff member: it is discarded when
 * nobody is signed in or another staff member reads it, so one person's
 * records never show in another's tab. Returning to a place already on the
 * trail goes back to it (later places are dropped), so Back never
 * oscillates between two records.
 */
export interface NavigationPlace {
  /** The address to return to, with its query (filters, tab). */
  path: string;
  /** One place per pathname: a record or a dashboard. */
  key: string;
  label: string;
}

interface StoredTrail {
  owner: string;
  places: NavigationPlace[];
}

const STORAGE_KEY = "hc-admin.navigation-trail";
const MAX_PLACES = 20;

const isPlace = (place: unknown): place is NavigationPlace =>
  typeof place === "object" &&
  place !== null &&
  typeof (place as NavigationPlace).path === "string" &&
  typeof (place as NavigationPlace).key === "string" &&
  typeof (place as NavigationPlace).label === "string";

const readStored = (): StoredTrail | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as StoredTrail).owner !== "string" ||
      !Array.isArray((parsed as StoredTrail).places)
    )
      return null;
    return {
      owner: (parsed as StoredTrail).owner,
      places: (parsed as StoredTrail).places.filter(isPlace),
    };
  } catch {
    return null;
  }
};

let trail: StoredTrail | null | undefined;

const store = (next: StoredTrail | null) => {
  trail = next ? { ...next, places: next.places.slice(-MAX_PLACES) } : null;
  try {
    if (trail)
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked; the trail then lasts for this page load.
  }
};

/** This owner's places; another owner's trail is discarded, never shown. */
const placesFor = (owner: string): NavigationPlace[] => {
  if (trail === undefined) trail = readStored();
  if (trail && trail.owner !== owner) store(null);
  return trail?.places ?? [];
};

/**
 * Notes a place for this staff member. Revisiting the latest place only
 * updates its address; returning to an earlier one drops what came after.
 */
export const recordNavigationPlace = (
  place: NavigationPlace,
  owner: string,
) => {
  const current = placesFor(owner);
  const earlier = current.findIndex((entry) => entry.key === place.key);
  store({
    owner,
    places:
      earlier >= 0
        ? [...current.slice(0, earlier), place]
        : [...current, place],
  });
};

/** The most recent other place before this one, if any. */
export const getPreviousNavigationPlace = (
  key: string,
  owner: string,
): NavigationPlace | null => {
  const current = placesFor(owner);
  const here = current.findIndex((entry) => entry.key === key);
  // On a place already on the trail, Back is the one before it.
  const before = here >= 0 ? current.slice(0, here) : current;
  return before.length > 0 ? before[before.length - 1] : null;
};

/** Forgets the trail (sign-out, tests). */
export const resetNavigationTrail = () => store(null);
